import {
  GridActionsCellItem,
  GridColumns,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators
} from '../../../../constants';
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import { IsBottomFromTechnicianCoordinator } from 'components/helpers/IsBottomFromTechnicianCoordinator';
import FileUploader from 'components/common/file-uploader';
import { ticketingService } from 'services/api';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import { isDirector } from 'components/helpers/IsDirector';
import { isTechnician } from 'components/helpers/IsTechnician';
import { isSupplier } from 'components/helpers/IsSupplier';
import { useState, useEffect } from 'react';
import { isTenantAdministrator } from 'components/helpers/IsTenantAdministrator';
import { IStatusOption } from 'interfaces';
import ConfirmationDeleteTableDialog from 'components/helpers/ConfirmationGridDelete';
import { IsSuperAdminGuard } from 'components/helpers/IsSuperAdmin';

// Fallback opzioni status ticket
const TICKET_STATUS_OPTIONS_FALLBACK: IStatusOption[] = [
  { value: 1, display_name: 'OPEN' },
  { value: 2, display_name: 'INTERVENTION_REQUESTED' },
  { value: 3, display_name: 'PHONE_RESOLVED' },
  { value: 4, display_name: 'TECHNICIAN_RESOLVED' },
  { value: 5, display_name: 'INTERVENTION_COMPLETED' },
  { value: 6, display_name: 'UNDETERMINED' },
  { value: 7, display_name: 'TECHNICIAN_CREATED' }
];

export const TicketColumns = (
  handleApprovation: boolean,
  approve?: (id: number, old_approvation_status?: boolean | null) => void,
  handleDelete?: any,
  showArchiveColumns = false
): GridColumns => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const isDisableEdit = isDirector() || isTechnician() || isSupplier();
  /** Data/ora editabili in griglia solo per amministratori; per i tecnici sono auto-generate */
  const canEditDates = isTenantAdministrator();
  const [statusOptions, setStatusOptions] = useState<IStatusOption[]>([]);
  const isSuperAdmin = IsSuperAdminGuard();

  useEffect(() => {
    ticketingService
      .options()
      .then((res) => {
        const raw =
          res?.actions?.POST?.status?.choices ??
          TICKET_STATUS_OPTIONS_FALLBACK.filter((c) => c.value !== 0);
        setStatusOptions(raw);
      })
      .catch(() => {
        setStatusOptions(TICKET_STATUS_OPTIONS_FALLBACK.filter((c) => c.value !== 0));
      });
  }, []);

  const columns: GridColumns = [
    {
      field: 'id',
      headerName: t(`global.number`),
      width: 50,
      filterOperators,
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <Link to={`/tenant/equipments/ticketing/${params.value}`}>
            <b className="text-secondary-500">#{params.value}</b>
          </Link>
        </div>
      )
    }
  ];

  if (handleApprovation) {
    columns.push({
      // 26/06/2025 : Stefano ha richiewto nuova pagina in cui gestire gli "tickect da approvare"
      field: 'approvation_status',
      headerName: t(`global.approvation-status`),
      width: 75,
      sortable: false,
      filterOperators: booleanFilterOperators,
      renderCell: (params: GridRenderCellParams) => (
        <div>
          {isDisableEdit ? (
            <VerifiedIcon color={params.row.approvation_status === true ? 'success' : 'disabled'} />
          ) : (
            <GridActionsCellItem
              icon={
                <Tooltip
                  title={t('actions.approve', { user: params.row.approvation_user })}
                  placement="top">
                  <VerifiedIcon
                    color={params.row.approvation_status === true ? 'success' : 'disabled'}
                  />
                </Tooltip>
              }
              onClick={() => approve(params.row.id, params.row.approvation_status)}
              label={t('actions.approve')}
            />
          )}
        </div>
      )
    });
  }

  columns.push(
    {
      field: 'equipment',
      headerName: t(`global.equipment`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'equipment_description',
      headerName: t(`${translationPrefix}.equipment_description`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'accessor_code',
      headerName: t(`global.accessor-code`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'accessor_description',
      headerName: t(`global.accessor-description`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'serial_number',
      headerName: t(`${translationPrefix}.serial_number`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: 'Problema Riscontrato',
      width: 260,
      filterOperators,
      sortable: false,
      renderCell: baseCell
    },
    {
      field: 'note',
      headerName: t(`global.notes`),
      width: 220,
      filterOperators,
      sortable: false,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      width: 150,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'created_at',
      headerName: t(`entities.tickets.created_at`),
      width: 200,
      filterOperators: dateFilterOperators,
      editable: canEditDates,
      type: 'datetime',
      renderCell: baseCell
    },
    {
      field: 'code_error',
      headerName: t(`${translationPrefix}.ticket_code_error`),
      width: 150,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'creation_user',
      headerName: t(`entities.tickets.creation_user`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'sent_at',
      headerName: t(`entities.tickets.sent_at`),
      width: 200,
      filterOperators: dateFilterOperators,
      editable: canEditDates,
      type: 'datetime',
      renderCell: baseCell
    },
    {
      field: 'approvation_user',
      headerName: t(`entities.tickets.approvation_user`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'resolved_at',
      headerName: t(`entities.tickets.resolved_at`),
      width: 200,
      filterOperators: dateFilterOperators,
      editable: canEditDates,
      type: 'datetime',
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      type: 'singleSelect',
      width: 100,
      valueGetter: (params) => {
        const r = params.row;
        if (r.status?.value != null) return r.status.value;
        const name = r.status_display;
        const opt = TICKET_STATUS_OPTIONS_FALLBACK.find((c) => c.display_name === name);
        return opt?.value ?? name;
      },
      valueOptions: statusOptions.map((type) => ({
        label: t(`entities.tickets.status_options.${type.display_name}`),
        value: type.value
      })),
      renderCell: (params: GridRenderCellParams) => (
        <div>
          {t(
            `entities.tickets.status_options.${
              params.row.status?.display_name ?? params.row.status_display
            }`
          )}
        </div>
      )
    },
    {
      field: 'compliant',
      headerName: t(`global.compliant`),
      width: 100,
      filterOperators: booleanFilterOperators,
      editable: !isDisableEdit,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'technical_report',
      headerName: t(`${translationPrefix}.technical_report`),
      renderCell: (params: GridRenderCellParams) => {
        const ticket = params.row;
        return (
          <IsBottomFromTechnicianCoordinator>
            <div>
              <FileUploader
                service={ticketingService}
                entityId={ticket.id}
                attachmentName={'technical_report'}
                filename={ticket.technical_report}
              />
            </div>
          </IsBottomFromTechnicianCoordinator>
        );
      },
      width: 200,
      filterable: false,
      sortable: false
    }
  );
  if (handleDelete && isSuperAdmin) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          <ConfirmationDeleteTableDialog
            key={`delete-${params.row.id}`}
            handleDelete={handleDelete}
            id={params.row.id}
          />
        ];
      }
    });
  }

  return columns;
};
